// The PROVIDER-REPORTED context window (arc 101 slice 02): the one place the
// dashboard decides whether the provider said anything at all, and how the
// number reads once it did.
//
// Why the dashboard cares: `contextLengthFor` (`packages/agent/src/compaction.ts`)
// used to GUESS the window from a prefix table, and the guess answered 1_000_000
// for models whose real window is 200_000 — compaction waited for 700_000 live
// tokens on a 200_000 window, never fired, and the context overflowed. The
// capture flow is the only producer of the real number for a dashboard-captured
// key, so a window dropped here is a window nothing else can supply.
//
// "The provider did not say" and "the provider said 200K" are DIFFERENT answers
// and stay different all the way to the screen: absent renders nothing — never a
// dash, never a placeholder, never a guess.

import { plausibleContextLength } from '@developerz/domain';
import type { ProviderModelOption } from './keys-types';

/**
 * A row as the enumeration route serializes it. `context_length` is contracted
 * as OMITTED when the provider reported none; `null` is tolerated here only
 * because a wire field is not a type system, and both mean the same thing.
 */
export type ProviderModelWireRow = {
  readonly id: string;
  readonly context_length?: number | null;
};

/**
 * Normalize one probe row into the option the picker offers.
 *
 * The window goes through the DOMAIN's `plausibleContextLength` — the same
 * function the capture route clamps with, so the picker cannot show a number the
 * server would refuse to act on, and an embedding row's 512 never renders as a
 * window. Not a second rule: one function, called at both ends of the trip.
 */
export function providerModelOption(row: ProviderModelWireRow): ProviderModelOption {
  const window = plausibleContextLength(row.context_length);
  // Conditional spread, never `context_length: undefined`:
  // `exactOptionalPropertyTypes` is on, and "absent" is the wire's own spelling.
  return { id: row.id, ...(window === undefined ? {} : { context_length: window }) };
}

/**
 * A token count as a person reads it — `8.2K`, `131K`, `200K`, `1M`, `1.5M`.
 *
 * One fraction digit below ten of a unit and none above it: `1.5M` is a
 * different window from `1M` and must survive, while `32.8K` is noise a reader
 * has to parse. Hand-rolled rather than `notation: 'compact'` so the output is
 * pinned by the tests below it and cannot move with an ICU build.
 */
export function humanTokens(tokens: number): string {
  const millions = tokens >= 1_000_000;
  const scaled = tokens / (millions ? 1_000_000 : 1_000);
  const digits = scaled < 10 ? 1 : 0;
  const value = new Intl.NumberFormat('en-US', { maximumFractionDigits: digits }).format(scaled);
  return `${value}${millions ? 'M' : 'K'}`;
}
